import type {ReactElement} from 'react';
import React from 'react';
import type {SubtitlesAnimationStyleDropdownProps} from './subtitles-animation-style-dropdown.types';
import styles from './subtitles-animation-style-dropdown.module.scss';
import {ContextualMenuDropdownOption} from '@Components/contextual-menu/components/contextual-menu-dropdown-option';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {SubtitleTemplateType} from '@PosterWhiteboard/items/transcript-item/subtitle/template-styles.types';
import {Thumbnail} from '@Components/thumbnail';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {getAssetUrl} from '@Utils/s3.util';
import {getCompatibleVideoFileExtension} from '@Utils/video.util';
import {ControlledList} from '@Components/controlled-list';
import {useAppDispatch} from '@/hooks';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {MODALS_IDS} from '@Components/modal-container';

const SUBTITLE_ANIMATION_STYLE_PREVIEWS_URL = 'poster-editor-thumbnails/ai-subtitles-animation-styles-previews/';

export function SubtitlesAnimationStyleDropdown({
  isMobile = false,
  isLoading = false,
  hideAnimationOptionsAndPresets = false,
  ...props
}: SubtitlesAnimationStyleDropdownProps): ReactElement<any> {
  const dispatch = useAppDispatch();

  const updateAppliedAnimationStyle = async (animationStyleId: SubtitleTemplateType): Promise<void> => {
    await props.updateAppliedAnimationStyle(animationStyleId);

    if (isMobile) {
      dispatch(closeModal(MODALS_IDS.MORE_OPTIONS_MODAL));
    }
  };

  const getThumbnail = (subtitleTemplateType: SubtitleTemplateType): ReactElement<any> => {
    return (
      <Thumbnail
        className={`radius-12 ${styles.thumbImage}`}
        data={{
          type: ThumbnailType.IMAGE,
          disabled: isLoading,
          imageSrc: getAssetUrl(`${SUBTITLE_ANIMATION_STYLE_PREVIEWS_URL}${subtitleTemplateType}.${getCompatibleVideoFileExtension('gif')}`),
          showSkeletonLoading: true,
        }}
      />
    );
  };

  const getAnimationStylesDropdownOptions = (): ControlledListItemType[] => {
    const animationStyleOptions: ControlledListItemType[] = [
      {
        id: SubtitleTemplateType.SIMPLE,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        isSelected: props.selectedAnimationStyle === SubtitleTemplateType.SIMPLE,
        text: window.i18next.t('pmwjs_simple'),
        subText: '',
        onClick: (): void => {
          void updateAppliedAnimationStyle(SubtitleTemplateType.SIMPLE);
        },
        thumbnail: {
          type: ThumbnailType.CUSTOM,
          content: getThumbnail(SubtitleTemplateType.SIMPLE),
        },
      },
      {
        id: SubtitleTemplateType.ONE_WORD,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        isSelected: props.selectedAnimationStyle === SubtitleTemplateType.ONE_WORD,
        text: window.i18next.t('pmwjs_one_word'),
        subText: '',
        onClick: (): void => {
          void updateAppliedAnimationStyle(SubtitleTemplateType.ONE_WORD);
        },
        thumbnail: {
          type: ThumbnailType.CUSTOM,
          content: getThumbnail(SubtitleTemplateType.ONE_WORD),
        },
      },
    ];

    if (!hideAnimationOptionsAndPresets) {
      animationStyleOptions.push(
        {
          id: SubtitleTemplateType.HIGHLIGHT_WORD,
          type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
          isSelected: props.selectedAnimationStyle === SubtitleTemplateType.HIGHLIGHT_WORD,
          text: window.i18next.t('pmwjs_dynamic'),
          subText: '',
          onClick: (): void => {
            void updateAppliedAnimationStyle(SubtitleTemplateType.HIGHLIGHT_WORD);
          },
          thumbnail: {
            type: ThumbnailType.CUSTOM,
            content: getThumbnail(SubtitleTemplateType.HIGHLIGHT_WORD),
          },
        },
        {
          id: SubtitleTemplateType.PROGRESS,
          type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
          isSelected: props.selectedAnimationStyle === SubtitleTemplateType.PROGRESS,
          text: window.i18next.t('pmwjs_progressive'),
          subText: '',
          onClick: (): void => {
            void updateAppliedAnimationStyle(SubtitleTemplateType.PROGRESS);
          },
          thumbnail: {
            type: ThumbnailType.CUSTOM,
            content: getThumbnail(SubtitleTemplateType.PROGRESS),
          },
        }
      );
    }

    return animationStyleOptions;
  };

  const getPopup = (): ReactElement<any> => {
    return (
      <ControlledList
        className={`spacing-m-0 spacing-p-b-2 ${isMobile ? '_full-width _full-height' : `_fit-height ${styles.popupDimensions}`}`}
        items={getAnimationStylesDropdownOptions()}
      />
    );
  };

  const getSelectorText = (): string => {
    switch (props.selectedAnimationStyle) {
      case SubtitleTemplateType.SIMPLE:
        return window.i18next.t('pmwjs_simple');
      case SubtitleTemplateType.HIGHLIGHT_WORD:
        return window.i18next.t('pmwjs_dynamic');
      case SubtitleTemplateType.PROGRESS:
        return window.i18next.t('pmwjs_progressive');
      case SubtitleTemplateType.ONE_WORD:
        return window.i18next.t('pmwjs_one_word');
      default:
        throw new Error(`Unhandled selectedAnimationStyle ${props.selectedAnimationStyle}`);
    }
  };

  return (
    <ContextualMenuDropdownOption
      customPopup={getPopup()}
      dropdownSelectorText={getSelectorText()}
      selectorClasses={!isMobile ? styles.selectorMaxWidth : ''}
      showModalForSmallScreen
      isSmallScreenModalBottomSheet
      isDisabled={isLoading}
      isLoading={isLoading}
      selectedItemId={props.selectedAnimationStyle}
      id="transcript-animation-style-option"
      label={window.i18next.t('pmwjs_animation')}
      modalTitle={window.i18next.t('pmwjs_animation')}
    />
  );
}
